<template>
  <div>
    <input style="display:none" accept="image/*" type="file" @change="readFile" ref="fileUploadInput" />
    <div v-if="localPreview && !showCroppie" class="mb-2">
      <img :src="localPreview" style="width:100%;object-fit:cover;" />
    </div>
    <ImageDisplay v-else-if="imageUrl && !showCroppie && !localPreview" :src="imageUrl" style="width:100%" />
    <v-card v-if="showCroppie" elevation="0">
      <div ref="imageContainer">
        <vue-croppie v-if="showCroppie" ref="croppie" :enable-exif="true" :enableOrientation="true" @result="result"
          :viewport="croppieViewport" :boundary="croppieViewportBoundary" :enable-resize="resize" :show-zoomer="true">
        </vue-croppie>
        <v-progress-linear v-if="uploading" indeterminate color="secondary"></v-progress-linear>
      </div>
      <v-card-actions class="px-0 pt-3">
        <PlusButton small @click="rotate(90)" :disabled="disableConfirm">
          <v-icon small class="mr-1">mdi-format-rotate-90</v-icon>
          <span v-if="!isSmallerDisplay()">{{ $t("message.rotate") }}</span>
        </PlusButton>
        <v-spacer></v-spacer>
        <PlusButton small intent="primary" @click="processImage()" :disabled="disableConfirm" class="mr-2">
          <v-icon v-if="uploading" small class="mr-1 rotate">mdi-sync</v-icon>
          <v-icon v-else small class="mr-1">mdi-check-circle-outline</v-icon>
          <span v-if="!isSmallerDisplay()">{{ $t("message.confirm") }}</span>
        </PlusButton>
        <PlusButton small :disabled="!showCroppie" @click="closeDialog()">
          <v-icon small class="mr-1">mdi-close-box-outline</v-icon>
          <span v-if="!isSmallerDisplay()">{{ $t("message.cancel") }}</span>
        </PlusButton>
      </v-card-actions>
    </v-card>

    <div v-if="!imageUrl && !showCroppie && !localPreview" class="flex flex-col" style="gap:1.5rem;">
      <div class="plusGreyDarken1--text">{{$t("message.academy_settings_admin_plus.cover_image_upload_explanation")}}</div>
      <div class="flex flex-col" style="gap:1rem;">
        <div class="plusGreyLighten5 flex justify-center align-center" style="height:6.5rem;gap:0.625rem;border:1px dashed #BDBDBD !important;"
          @dragover.prevent
          @dragenter.prevent
          @drop.prevent="handleDrop"
          >
          <div class="plusGreyDarken1--text">{{$t("message.academy_settings_admin_plus.drag_and_drop_or")}}</div>
          <PlusButton intent="link" @click="openFileUploader()">{{$t("message.academy_settings_admin_plus.upload_image")}}</PlusButton>
        </div>
        <div class="plusGreyDarken1--text" style="font-size:0.875rem">
          {{$t("message.academy_settings_admin_plus.recommended_size")}}: 1200 × 400 px (3:1) &mdash; {{$t("message.academy_settings_admin_plus.cover_image_focal_hint")}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api2";
import PlusButton from "@/components/plus/PlusButton";

export default {
  components: {
    PlusButton
  },
  data() {
    return {
      image: null,
      cropped: null,
      localPreview: null,
      cropImageDialog: false,
      disableConfirm: true,
      uploadingMessage: null,
      resize: false,
      uploading: false,
      showCroppie: false,
      toBigMessage: null,
      croppieViewport: { width: 570, height: 190, type: 'rectangle' },
      croppieViewportBoundary: { width: 600, height: 280 },
    };
  },
  props: ["imageUrl", "enableResize"],
  watch: {
    imageUrl(newVal) {
      if (!newVal) this.localPreview = null;
    }
  },
  created() {
    if (this.enableResize)
      this.resize = this.enableResize;
  },
  methods: {
    getCroppieViewport() {
      const rootWidth = this.$el && this.$el.clientWidth ? this.$el.clientWidth : 600;
      const containerWidth = Math.min(rootWidth, 600);
      const isMobile = this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm';
      const viewportWidth = containerWidth - 40;
      const viewportHeight = Math.round(viewportWidth / 3);
      const boundaryHeight = viewportHeight + (isMobile ? 60 : 90);
      this.croppieViewport = { width: viewportWidth, height: viewportHeight, type: 'rectangle' };
      this.croppieViewportBoundary = { width: containerWidth, height: boundaryHeight };
    },
    handleDrop(e) {
      const droppedFiles = e.dataTransfer.files;
      if (!droppedFiles.length) return;
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFiles[0]);
      this.$refs.fileUploadInput.files = dataTransfer.files;
      this.readFile();
    },
    closeDialog() {
      this.cropImageDialog = false;
      this.clearCroppie();
    },
    clearCroppie() {
      this.image = null;
      this.cropped = null;
      this.localPreview = null;
      this.disableConfirm = true;
      this.uploading = false;
      this.showCroppie = false;
      this.$refs.fileUploadInput.value = null;
      if (this.$refs.croppie) this.$refs.croppie.destroy();
    },
    openFileUploader() {
      this.$refs.fileUploadInput.click();
    },
    readFile() {
      this.toBigMessage = null;
      this.getCroppieViewport();
      this.showCroppie = true;
      this.$nextTick(() => {
        if (this.$refs.fileUploadInput.files && this.$refs.fileUploadInput.files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.$refs.croppie.bind({ url: e.target.result });
          };
          reader.readAsDataURL(this.$refs.fileUploadInput.files[0]);
        }
        this.disableConfirm = false;
      });
    },
    processImage() {
      this.uploading = true;
      this.disableConfirm = true;
      let options = {
        format: "png",
        size: "original",
        circle: false,
        quality: 1
      };
      const vm = this;
      this.$refs.croppie.result(options, output => {
        vm.cropped = output;
        vm.localPreview = output;
        vm.showCroppie = false;
        if (vm.$refs.croppie) vm.$refs.croppie.destroy();
        vm.uploadImage(output);
      });
    },
    result(output) {
      this.cropped = output;
    },
    uploadImage(ImageURL) {
      let block = ImageURL.split(";");
      let contentType = block[0].split(":")[1];
      let realData = block[1].split(",")[1];
      let blob = this.b64toBlob(realData, contentType);

      try {
        const d = new Date();
        const formData = new FormData();
        const millis = d.getTime();
        const imgExtension = contentType.toLowerCase().split('/')[1];
        formData.append("image", blob, 'uploaded-image-user-admin-' + millis + '.' + imgExtension);

        Api().post("content/image/admin", formData)
          .then(res => {
            if (res) {
              this.uploading = false;
              this.image = null;
              this.cropped = null;
              this.localPreview = res.data.original || res.data.location;
              this.disableConfirm = true;
              this.showCroppie = false;
              this.$refs.fileUploadInput.value = null;
              this.$emit('uploaded', res.data);
            }
          })
          .catch(err => {
            this.uploading = false;
            if (err)
              this.showAlert('error', this.$t('message.oops_not_working'));
          });
      } catch (err) {
        this.showAlert('error', this.$t('message.oops_not_working'));
      }
    },
    rotate(rotationAngle) {
      this.$refs.croppie.rotate(rotationAngle);
    },
    b64toBlob(b64Data, contentType, sliceSize) {
      contentType = contentType || "";
      sliceSize = sliceSize || 512;

      var byteCharacters = atob(b64Data);
      var byteArrays = [];

      for (
        var offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      var blob = new Blob(byteArrays, { type: contentType });
      return blob;
    }
  }
};
</script>

<style scoped></style>
